import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import {
  FaMoon,
  FaSun,
  FaCheckCircle,
  FaDumbbell,
  FaAppleAlt,
  FaBolt,
  FaRobot,
  FaHome,
  FaEdit,
  FaStar,
} from 'react-icons/fa';

import Plans from './Plans';
import Login from './login';
import PlanWizard from './PlanWizard';
import Summary from './Summary';
import Payment from './Payment';
import Dashboard from './Dashboard';
import ActivePlan from './ActivePlan';
import History from './History';
import Profile from './Profile';
import NotFound from './NotFound';
import Feedback from './Feedback';
import AdminLogin from './Admin/Login';
import AdminDashboard from './Admin/Dashboard';
import AdminPlans from './Admin/Plans';
import AdminUsers from './Admin/Users';
import AdminFeedback from './Admin/Feedback';
import PlanDetails from './Admin/PlanDetails';
import Terms from './Terms';
import VideoReports from './Admin/VideoReports';
import VideoReportDetails from './Admin/VideoReportDetails';
import ContactUs from './ContactUs';
import PaymentResult from './PaymentResult';
import Faq from './Faq';
import ZibalTrustBadge from './component/ZibalTrustBadge';
import EnamadBadge from './component/EnamadBadge';
import DiscountStrip from './component/DiscountStrip';
import DownloadApp from './DownloadApp';
import RevisionRequest from './RevisionRequest';
import RevisionDetails from './RevisionDetails';
import PlanPreview from './PlanPreview';
import InstallPwaButton from './component/InstallPwaButton';
import PwaUpdatePrompt from './component/PwaUpdatePrompt';
import ReferralScreen from './ReferralScreen';
import BodyAnalysisLanding from "./pages/campaign/BodyAnalysisLanding";
import WizardIntroPage from "./pages/campaign/WizardIntroPage";
import CampaignPlanWizard from "./pages/campaign/CampaignPlanWizard";
import AnalysisResultPage from "./pages/campaign/AnalysisResultPage";
import ProgramPreviewPage from "./pages/campaign/ProgramPreviewPage";
import CampaignOtpPage from "./pages/campaign/CampaignOtpPage";
import CampaignOfferPage from "./pages/campaign/CampaignOfferPage";
import PaymentPreparingPage from "./pages/campaign/PaymentPreparingPage";

const API_URL = process.env.REACT_APP_API_URL || 'https://api.mogym.ir';

const heroSlides = [
  {
    badge: 'مربی AI شخصی',
    title: 'برنامه‌ای که دقیقاً برای بدن تو ساخته میشه',
    subtitle: 'بر اساس هدف، شرایط بدنی، سطح تمرین، تجهیزات و محدودیت‌های واقعی تو',
  },
  {
    badge: 'خانه یا باشگاه',
    title: 'برنامه متناسب با شرایط واقعی تو',
    subtitle: 'چه در خانه تمرین کنی چه باشگاه، خروجی بر اساس امکانات خودت ساخته میشه',
  },
  {
    badge: 'تمرین + تغذیه',
    title: 'مسیرت رو با یک برنامه کامل شروع کن',
    subtitle: 'تمرین، رژیم، و در صورت نیاز مکمل؛ همه در یک مسیر منسجم',
  },
];

const featureItems = [
  { title: 'تحلیل هوشمند', desc: 'اطلاعاتت با منطق AI و دید مربیگری بررسی می‌شود.', icon: <FaRobot /> },
  { title: 'تمرین اختصاصی', desc: 'برنامه بر اساس هدف، سطح و شرایط بدنی تو ساخته می‌شود.', icon: <FaDumbbell /> },
  { title: 'رژیم غذایی دقیق', desc: 'تغذیه متناسب با هدفت و سبک زندگی‌ات تنظیم می‌شود.', icon: <FaAppleAlt /> },
  { title: 'خانه یا باشگاه', desc: 'خروجی با توجه به تجهیزات و محل تمرینت تنظیم می‌شود.', icon: <FaHome /> },
  { title: 'قابل اصلاح', desc: 'اگر لازم باشد، امکان ثبت درخواست اصلاح برنامه را داری.', icon: <FaEdit /> },
];

const previewPlans = [
  {
    title: 'چربی‌سوزی سریع',
    subtitle: '۳ جلسه در هفته • مبتدی',
    items: ['اسکوات • ۳×۱۲', 'پرس سینه • ۳×۱۰', 'کرانچ • ۳×۱۵'],
  },
  {
    title: 'عضله‌سازی',
    subtitle: '۴ جلسه در هفته • متوسط',
    items: ['ددلیفت • ۴×۸', 'بارفیکس • ۳×۱۰', 'پرس سرشانه • ۳×۱۲'],
  },
  {
    title: 'تمرین در خانه',
    subtitle: 'بدون تجهیزات',
    items: ['اسکوات وزن بدن • ۴×۱۵', 'شنا • ۳×۱۲', 'پلانک • ۳ ست'],
  },
];

const reviews = [
  { name: 'ستاره', text: 'خیلی خوب بود که برنامه واقعاً بر اساس شرایط خودم ساخته شده بود.' },
  { name: 'مریم', text: 'برای اولین بار حس کردم یه برنامه عمومی نگرفتم و دقیقاً برای خودمه.' },
  { name: 'محمد', text: 'روند ساخت برنامه و خروجی نهایی خیلی حرفه‌ای و قابل اعتماد بود.' },
];

function formatToman(n) {
  return `${Number(n || 0).toLocaleString('fa-IR')} تومان`;
}

function getPriceValue(priceItem) {
  if (!priceItem) return 0;
  if (typeof priceItem === 'number') return priceItem;
  return Number(priceItem.price ?? 0);
}

function getCompareAtValue(priceItem) {
  if (!priceItem || typeof priceItem !== 'object') return null;
  return priceItem.compareAtPrice == null ? null : Number(priceItem.compareAtPrice);
}

function PriceBlock({ item }) {
  const price = getPriceValue(item);
  const compareAt = getCompareAtValue(item);

  if (!price) return <p className="mt-3 text-sm text-slate-400">قیمت نامشخص</p>;

  return (
    <div className="mt-4">
      {compareAt > price && (
        <div className="text-sm text-slate-400 line-through mb-1">
          {formatToman(compareAt)}
        </div>
      )}
      <div className="text-xl font-extrabold text-purple-400">{formatToman(price)}</div>
    </div>
  );
}

function normalizeActiveCampaign(data) {
  if (!data || typeof data !== 'object') return { enabled: false };
  return {
    enabled: data.enabled ?? data.Enabled ?? false,
    campaign: data.campaign ?? data.Campaign ?? null,
    content: data.content ?? data.Content ?? null,
    capacity: data.capacity ?? data.Capacity ?? null,
  };
}

function HomePage() {
  const [dark, setDark] = useState(true);
  const [heroIndex, setHeroIndex] = useState(0);
  const [prices, setPrices] = useState(null);
  const [loadingPrices, setLoadingPrices] = useState(true);
  const [activeCampaign, setActiveCampaign] = useState({ enabled: false });
  const [trustStats, setTrustStats] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroIndex((i) => (i + 1) % heroSlides.length);
    }, 4500);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/prices`)
      .then((r) => r.json())
      .then((d) => setPrices(d?.result ?? null))
      .catch(() => setPrices(null))
      .finally(() => setLoadingPrices(false));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/api/campaigns/active?placement=summary`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setActiveCampaign(normalizeActiveCampaign(d)))
      .catch(() => setActiveCampaign({ enabled: false }));
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/landing/trust-stats`)
      .then((r) => r.json())
      .then((d) => setTrustStats(d?.result ?? null))
      .catch(() => setTrustStats(null));
  }, []);

  const fullPrice =
    typeof prices?.normal?.full === 'object'
      ? Number(prices?.normal?.full?.price ?? 0)
      : Number(prices?.normal?.full ?? 0);

  const planCards = useMemo(
    () => [
      {
        title: 'برنامه تمرینی',
        requestedPlan: 1,
        priceItem: prices?.normal?.workout,
        desc: 'فقط برنامه تمرین اختصاصی',
        features: ['تمرین متناسب با سطح تو', 'مناسب خانه یا باشگاه'],
      },
      {
        title: 'برنامه رژیم و تغذیه',
        requestedPlan: 2,
        priceItem: prices?.normal?.diet,
        desc: 'فقط برنامه تغذیه اختصاصی',
        features: ['رژیم متناسب با هدف', 'گزینه‌های قابل جایگزینی'],
      },
      {
        title: 'تمرین و رژیم غذایی',
        requestedPlan: 3,
        priceItem: prices?.normal?.full,
        desc: 'کامل‌ترین گزینه برای نتیجه بهتر',
        features: ['تمرین + رژیم کامل', 'بهترین گزینه برای شروع جدی'],
        recommended: true,
      },
    ],
    [prices]
  );

  const handleAuth = () => {
    const token = localStorage.getItem('token');
    navigate(token ? '/dashboard' : '/login');
  };

  const handleFreePlanClick = () => {
    const token = localStorage.getItem('token');

    const freeNavState = {
      journeyMode: 'sample',
      requestedPlan: 3,
      planType: 0,
      price: fullPrice,
      free: true,
      initialStep: 2,
      prices: prices ?? null,
      campaignId: null,
      campaignKey: null,
      campaignPrice: 0,
    };

    localStorage.setItem('freeRequestedPlan', '3');
    localStorage.setItem('freePlanType', '0');
    localStorage.setItem('freePrice', String(fullPrice || 0));
    localStorage.setItem('planWizardState', JSON.stringify(freeNavState));

    if (!token) {
      localStorage.setItem(
        'redirectAfterLogin',
        JSON.stringify({
          to: '/PlanWizard',
          state: freeNavState,
        })
      );
      navigate('/login');
      return;
    }

    navigate('/PlanWizard', { state: freeNavState });
  };

  const handleRegularPlan = (requestedPlan) => {
    navigate('/plans', {
      state: {
        preselectedPlan: requestedPlan,
        forceShowRegularPlans: true,
      },
    });
  };

  const stats = trustStats
    ? [
        { value: `+${Number(trustStats.activeUsers || 0).toLocaleString('fa-IR')}`, label: 'کاربر فعال' },
        { value: `+${Number(trustStats.generatedPlans || 0).toLocaleString('fa-IR')}`, label: 'برنامه ساخته‌شده' },
        { value: String(trustStats.userSatisfaction ?? '۴.۳').replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]), label: 'رضایت کاربران' },
      ]
    : [
        { value: '+۱,۰۰۰', label: 'کاربر فعال' },
        { value: '+۱۰۰', label: 'برنامه ساخته‌شده' },
        { value: '۴.۳', label: 'رضایت کاربران' },
      ];

  const slide = heroSlides[heroIndex];
  const showCampaign = activeCampaign?.enabled === true && !!activeCampaign?.content;

  return (
    <div dir="rtl" className={dark ? 'dark' : ''}>
      <div className="min-h-screen font-vazir bg-[#f8faff] dark:bg-[#070B14] text-slate-900 dark:text-white overflow-hidden">
        <div className="fixed top-20 right-[-80px] w-72 h-72 rounded-full bg-purple-500/20 blur-3xl pointer-events-none" />
        <div className="fixed top-80 left-[-80px] w-72 h-72 rounded-full bg-blue-500/20 blur-3xl pointer-events-none" />

        <DiscountStrip />

        <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/75 dark:bg-[#0A0F1E]/80 border-b border-slate-200/70 dark:border-white/10">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center gap-3">
              <img src="/logo.png" alt="Mogym" className="w-12 h-12" />
              <div className="hidden sm:block text-right">
                <div className="font-extrabold text-lg">موجیم</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">باشگاهی به وسعت ایران</div>
              </div>
            </button>

            <nav className="hidden lg:flex items-center gap-7 text-sm text-slate-600 dark:text-slate-300 ">
              <a href="#plans" className="hover:text-purple-500">پلن‌ها</a>
              <a href="#how" className="hover:text-purple-500">روند کار</a>
              <a href="#preview" className="hover:text-purple-500">نمونه خروجی</a>
              <a href="#trust" className="hover:text-purple-500">اعتماد کاربران</a>
            </nav>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setDark((v) => !v)}
                className="w-10 h-10 rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#111827] flex items-center justify-center"
              >
                {dark ? <FaSun /> : <FaMoon />}
              </button>

              <button
                onClick={handleAuth}
                className="px-4 py-2.5 rounded-2xl bg-gradient-to-l from-purple-600 to-blue-500 text-white text-sm font-bold shadow-lg shadow-purple-500/20"
              >
                ورود / ثبت‌نام
              </button>
            </div>
          </div>
        </header>

        <main className="relative z-10">
          <section className="max-w-7xl mx-auto px-4 pt-12 pb-10 lg:pt-20 lg:pb-16 grid lg:grid-cols-2 gap-10 items-center">
            <div className="text-center lg:text-right">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-purple-500/20 bg-purple-500/10 text-sm font-bold text-purple-400 mb-6">
                <FaBolt />
                {slide.badge}
              </div>

              <h1 className="text-4xl md:text-6xl font-black leading-[1.35] tracking-tight">
                {slide.title}
              </h1>

              <p className="mt-5 text-base md:text-lg leading-8 text-slate-600 dark:text-slate-300 max-w-2xl mx-auto lg:mx-0">
                {slide.subtitle}
              </p>
<div className="mt-8 flex flex-col items-center gap-3">

  <div className="flex flex-col sm:flex-row-reverse lg:flex-row gap-3 justify-center lg:justify-start">
    
    <button
      onClick={() => navigate('/plans')}
      className="px-8 py-4 rounded-2xl bg-gradient-to-l from-purple-600 to-blue-500 text-white font-extrabold shadow-xl shadow-purple-500/25 hover:scale-[1.02] transition"
    >
      شروع ساخت برنامه اختصاصی
    </button>

    <button
      onClick={() => navigate('/app')}
      className="px-8 py-4 rounded-2xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 font-bold hover:border-purple-400 transition"
    >
      دانلود اپلیکیشن
    </button>

  </div>

  <InstallPwaButton
    label="نصب سریع موجیم روی موبایل"
    image="/pwa.svg"
  />

</div>
              <div className="mt-5 text-xs text-slate-500 dark:text-slate-400">
                کمتر از ۲ دقیقه • تمرین + تغذیه • مناسب خانه یا باشگاه
              </div>

              <div className="flex justify-center lg:justify-start gap-2 mt-7">
                {heroSlides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setHeroIndex(i)}
                    className={`h-2 rounded-full transition-all ${
                      i === heroIndex ? 'w-8 bg-blue-500' : 'w-2 bg-slate-300 dark:bg-slate-700'
                    }`}
                  />
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="rounded-[2rem] border border-white/10 bg-white/80 dark:bg-[#10182A]/90 shadow-2xl p-5 md:p-7 backdrop-blur-xl">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex gap-2">
                    <span className="px-3 py-1 rounded-full bg-purple-500/10 text-purple-400 text-xs font-bold">AI</span>
                    <span className="px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-bold">شخصی‌سازی</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm font-bold">
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
                    در حال تحلیل اطلاعات
                  </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-3 mb-5">
                  {['هدف', 'شرایط بدنی', 'تجهیزات'].map((x) => (
                    <div key={x} className="rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 p-4 text-center text-sm">
                      {x}
                    </div>
                  ))}
                </div>

                {['سن', 'قد', 'وزن', 'هدف', 'تجهیزات'].map((x) => (
                  <div
                    key={x}
                    className="flex items-center justify-between rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.03] p-4 mb-3"
                  >
                    <div>
                      <div className="font-bold">{x}</div>
                      <div className="text-xs text-slate-500 mt-1">تایید شد</div>
                    </div>
                    <span className="w-8 h-8 rounded-full bg-green-500/15 text-green-500 flex items-center justify-center">
                      ✓
                    </span>
                  </div>
                ))}

                <div className="mt-5 flex items-center justify-between text-sm">
                  <span className="text-slate-500 dark:text-slate-400">ساخت برنامه اختصاصی</span>
                  <span className="font-bold text-blue-400">۱۰۰٪</span>
                </div>
                <div className="mt-2 h-3 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                  <div className="h-full w-full bg-gradient-to-l from-purple-600 to-blue-500" />
                </div>

                <div className="mt-5 rounded-2xl border border-green-500/20 bg-green-500/10 p-4 flex items-center justify-between">
                  <div>
                    <div className="font-bold text-green-500">برنامه شما آماده شد</div>
                    <div className="text-xs text-green-600 dark:text-green-300 mt-1">
                      خروجی بر اساس اطلاعات واقعی شما ساخته شد
                    </div>
                  </div>
                  <span className="px-3 py-1 rounded-full bg-white/60 dark:bg-white/10 text-xs font-bold">آماده</span>
                </div>
              </div>
            </div>
          </section>

          <section className="max-w-5xl mx-auto px-4 py-10">
            <div className="rounded-[2rem] bg-gradient-to-br from-[#181F38] to-[#0C1222] border border-white/10 p-6 md:p-8 shadow-xl">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <span className="inline-block px-3 py-1 rounded-full bg-blue-500 text-white text-xs font-bold mb-4">
                    پیش‌نمایش قبل از خرید
                  </span>
                  <h2 className="text-2xl md:text-3xl font-black">اول نمونه برنامه‌ات را ببین</h2>
                  <p className="mt-3 leading-8 text-slate-300">
                    قبل از تصمیم نهایی، یک پیش‌نمایش شخصی‌سازی‌شده از سبک برنامه مناسب خودت ببین.
                  </p>
                </div>
                <button
                  onClick={handleFreePlanClick}
                  className="shrink-0 px-7 py-4 rounded-2xl bg-gradient-to-l from-blue-500 to-purple-600 text-white font-extrabold"
                >
                  مشاهده نمونه برنامه
                </button>
              </div>
            </div>
          </section>

          <section id="plans" className="max-w-7xl mx-auto px-4 py-12">
            <SectionTitle
              title="برنامه مناسب خودتو انتخاب کن"
              subtitle="بر اساس هدفت، بهترین گزینه رو انتخاب کن و وارد مسیر ساخت برنامه بشو"
            />

            {showCampaign ? (
              <div className="max-w-3xl mx-auto rounded-[2rem] p-6 md:p-8 border border-purple-500/20 bg-gradient-to-br from-purple-950/80 to-slate-900 shadow-xl">
                <div className="flex items-center justify-between gap-4 mb-4">
                  <span className="px-3 py-1 rounded-full bg-gradient-to-l from-purple-600 to-blue-500 text-white text-xs font-bold">
                    پیشنهاد ویژه
                  </span>
                  {activeCampaign?.capacity?.remainingText && (
                    <span className="text-sm text-slate-300">{activeCampaign.capacity.remainingText}</span>
                  )}
                </div>
                <h3 className="text-2xl font-black">
                  {activeCampaign?.content?.title || activeCampaign?.content?.headline || 'پیشنهاد ویژه موجیم'}
                </h3>
                <p className="mt-3 text-slate-300 leading-8">
                  {activeCampaign?.content?.subtitle || activeCampaign?.content?.subTitle || activeCampaign?.content?.description}
                </p>
                {activeCampaign?.content?.priceText && (
                  <div className="mt-5 text-2xl font-black text-purple-300">
                    {activeCampaign.content.priceText}
                  </div>
                )}
                <button
                  onClick={() => navigate('/plans')}
                  className="mt-6 w-full py-4 rounded-2xl bg-gradient-to-l from-purple-600 to-blue-500 text-white font-extrabold"
                >
                  {activeCampaign?.content?.ctaText || 'مشاهده پیشنهاد ویژه'}
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-5">
                {planCards.map((plan) => (
                  <div
                    key={plan.requestedPlan}
                    className={`relative rounded-[2rem] border p-6 shadow-xl ${
                      plan.recommended
                        ? 'border-purple-500/30 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-[#1F1A3A] dark:to-[#0F1527]'
                        : 'border-slate-200 dark:border-white/10 bg-white dark:bg-[#10182A]'
                    }`}
                  >
                    {plan.recommended && (
                      <span className="absolute top-5 left-5 px-3 py-1 rounded-full bg-gradient-to-l from-purple-600 to-blue-500 text-white text-xs font-bold">
                        پیشنهادی
                      </span>
                    )}
                    <h3 className="text-xl font-black">{plan.title}</h3>
                    <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{plan.desc}</p>

                    <div className="mt-5 space-y-2">
                      {plan.features.map((f) => (
                        <div key={f} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                          <FaCheckCircle className="text-green-500" />
                          {f}
                        </div>
                      ))}
                    </div>

                    {loadingPrices ? (
                      <div className="mt-5 h-7 w-32 rounded-lg bg-slate-200 dark:bg-white/10 animate-pulse" />
                    ) : (
                      <PriceBlock item={plan.priceItem} />
                    )}

                    <button
                      onClick={() => handleRegularPlan(plan.requestedPlan)}
                      className={`mt-6 w-full py-3.5 rounded-2xl font-extrabold ${
                        plan.recommended
                          ? 'bg-gradient-to-l from-purple-600 to-blue-500 text-white'
                          : 'bg-slate-100 dark:bg-white/10'
                      }`}
                    >
                      انتخاب این برنامه
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section id="how" className="max-w-6xl mx-auto px-4 py-12">
            <SectionTitle title="فقط در ۳ مرحله ساده" subtitle="مسیر ساخت برنامه سریع، شفاف و بدون پیچیدگی طراحی شده" />
            <div className="grid md:grid-cols-3 gap-5">
              {[
                ['۱', 'اطلاعاتتو وارد کن', 'هدف، شرایط بدنی و تجهیزاتت را مشخص می‌کنی.'],
                ['۲', 'هوش مصنوعی تحلیل می‌کند', 'اطلاعاتت با منطق مربیگری و AI بررسی می‌شود.'],
                ['۳', 'برنامت ساخته می‌شود', 'خروجی تمرین و تغذیه متناسب با شرایطت آماده می‌شود.'],
              ].map(([num, title, desc], i) => (
                <div key={num} className={`rounded-[2rem] p-6 border shadow-lg ${
                  i === 1
                    ? 'bg-gradient-to-br from-purple-50 to-blue-50 dark:from-[#251846] dark:to-[#10182E] border-purple-500/25'
                    : 'bg-white dark:bg-[#10182A] border-slate-200 dark:border-white/10'
                }`}>
                  <div className="w-11 h-11 rounded-full bg-gradient-to-l from-purple-600 to-blue-500 text-white flex items-center justify-center font-black mb-4">
                    {num}
                  </div>
                  <h3 className="text-lg font-black">{title}</h3>
                  <p className="mt-2 leading-7 text-sm text-slate-500 dark:text-slate-400">{desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="preview" className="max-w-7xl mx-auto px-4 py-12">
            <SectionTitle
              title="برنامه‌ها برای هر هدف فرق می‌کنند"
              subtitle="خروجی موجیم بسته به هدف، سطح و شرایط تمرین برای هر کاربر متفاوت ساخته می‌شود"
            />
            <div className="grid md:grid-cols-3 gap-5">
              {previewPlans.map((plan) => (
                <div key={plan.title} className="rounded-[2rem] bg-white dark:bg-[#10182A] border border-slate-200 dark:border-white/10 p-6 shadow-lg">
                  <span className="inline-block px-3 py-1 rounded-full bg-gradient-to-l from-purple-600 to-blue-500 text-white text-xs font-bold mb-4">
                    {plan.title}
                  </span>
                  <h3 className="text-xl font-black">{plan.title}</h3>
                  <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{plan.subtitle}</p>
                  <div className="mt-5 space-y-3">
                    {plan.items.map((x) => (
                      <div key={x} className="rounded-2xl bg-slate-50 dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 px-4 py-3 text-sm">
                        {x}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-4 py-12">
            <SectionTitle title="چرا موجیم؟" subtitle="موجیم فقط یک لیست تمرین ثابت نمی‌دهد؛ خروجی بر اساس شرایط واقعی تو ساخته می‌شود" />
            <div className="grid md:grid-cols-5 gap-4">
              {featureItems.map((f, i) => (
                <div
                  key={f.title}
                  className={`rounded-[2rem] p-5 border shadow-lg ${
                      'bg-white dark:bg-[#10182A] border-slate-200 dark:border-white/10'
                  }`}
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-l from-purple-600 to-blue-500 text-white flex items-center justify-center mb-4">
                    {f.icon}
                  </div>
                  <h3 className="font-black">{f.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-slate-500 dark:text-slate-400">{f.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="trust" className="max-w-7xl mx-auto px-4 py-12">
            <SectionTitle title="حس اعتماد از تجربه واقعی میاد" subtitle="وقتی خروجی برای خود کاربر ساخته می‌شود، تجربه نهایی هم متفاوت‌تر است" />

            <div className="grid md:grid-cols-3 gap-5 mb-8">
              {stats.map((s, i) => (
                <div key={s.label} className={`rounded-[2rem] p-6 text-center border shadow-lg ${
                  i === 1
                    ? 'bg-gradient-to-br from-purple-50 to-blue-50 dark:from-[#231844] dark:to-[#0F1629] border-purple-500/25'
                    : 'bg-white dark:bg-[#10182A] border-slate-200 dark:border-white/10'
                }`}>
                  <div className="text-3xl font-black">{s.value}</div>
                  <div className="mt-2 text-sm text-slate-500 dark:text-slate-400">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              {reviews.map((r) => (
                <div key={r.name} className="rounded-[2rem] bg-white dark:bg-[#10182A] border border-slate-200 dark:border-white/10 p-6 shadow-lg">
                  <FaStar className="text-yellow-400 mb-4" />
                  <p className="leading-8 text-slate-600 dark:text-slate-300">{r.text}</p>
                  <div className="mt-5 font-black">{r.name}</div>
                  <div className="text-xs text-slate-400 mt-1">کاربر موجیم</div>
                </div>
              ))}
            </div>
          </section>

          <section className="max-w-5xl mx-auto px-4 py-14">
            <div className="rounded-[2rem] bg-gradient-to-br from-purple-950 to-slate-900 border border-purple-500/20 text-white p-8 md:p-10 text-center shadow-2xl">
              <h2 className="text-3xl md:text-4xl font-black">شروع کن</h2>
              <p className="mt-4 leading-8 text-slate-300 max-w-2xl mx-auto">
                حالا که مسیر، نمونه خروجی و مزیت‌ها رو دیدی، می‌تونی خیلی سریع وارد ساخت برنامه شخصی خودت بشی.
              </p>
              <button
                onClick={() => navigate('/plans')}
                className="mt-7 px-9 py-4 rounded-2xl bg-gradient-to-l from-purple-600 to-blue-500 text-white font-extrabold shadow-xl"
              >
                شروع ساخت برنامه من
              </button>
            </div>
          </section>
        </main>

        <footer className="relative z-10 max-w-7xl mx-auto px-4 pb-8">
          <div className="rounded-[2rem] bg-white dark:bg-[#10182A] border border-slate-200 dark:border-white/10 p-6 text-center">
            <div className="font-black text-xl">موجیم</div>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              برنامه تمرینی و رژیم غذایی هوشمند برای رسیدن به بهترین نسخه خودت
            </p>

            <div className="flex justify-center gap-5 flex-wrap mt-5 text-sm">
              <a href="/terms">شرایط و مقررات</a>
              <a href="/faq">سوالات متداول</a>
              <a href="/contact-us">ارتباط با ما</a>
              <a href="/app">دانلود اپلیکیشن</a>
            </div>

{/* <div className="flex items-center justify-center gap-6 flex-wrap">
  <div className="flex items-center justify-center">
    <ZibalTrustBadge />
  </div>

  <div className="flex items-center justify-center">
    <EnamadBadge size={90} />
  </div>
</div> */}

            <div className="mt-5 pt-5 border-t border-slate-200 dark:border-white/10 text-xs text-slate-400">
              © {new Date().getFullYear()} Mogym. تمام حقوق محفوظ است
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

function SectionTitle({ title, subtitle }) {
  return (
    <div className="text-center mb-8">
      <h2 className="text-2xl md:text-4xl font-black">{title}</h2>
      <p className="mt-3 text-sm md:text-base text-slate-500 dark:text-slate-400 leading-8 max-w-2xl mx-auto">
        {subtitle}
      </p>
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <PwaUpdatePrompt />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/plans" element={<Plans />} />
        <Route path="/Login" element={<Login />} />
        <Route path="/login" element={<Login />} />
        <Route path="/PlanWizard" element={<PlanWizard />} />
        <Route path="/summary" element={<Summary />} />
        <Route path="/payment" element={<Payment />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/active-plan" element={<ActivePlan />} />
        <Route path="/history" element={<History />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/plans" element={<AdminPlans />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/feedback" element={<AdminFeedback />} />
        <Route path="/admin/plan/:id" element={<PlanDetails />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/admin/videoReports" element={<VideoReports />} />
        <Route path="/admin/reports/:id" element={<VideoReportDetails />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/payment-result" element={<PaymentResult />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/app" element={<DownloadApp />} />
        <Route path="/revision-request/:planId" element={<RevisionRequest />} />
        <Route path="/revision-details/:planId" element={<RevisionDetails />} />
        <Route path="*" element={<NotFound />} />
        <Route path="/plan-preview" element={<PlanPreview />} />
        <Route path="/referral" element={<ReferralScreen/>}/>
        <Route path="/body-analysis" element={<BodyAnalysisLanding />} />
        <Route path="/body-analysis/wizard" element={<CampaignPlanWizard />} />
        <Route path="/body-analysis/result" element={<AnalysisResultPage />} />
        <Route path="/body-analysis/preview" element={<ProgramPreviewPage />} />
        <Route path="/body-analysis/offer" element={<CampaignOfferPage />} />
        <Route path="/body-analysis/payment-preparing" element={<PaymentPreparingPage />} />
        <Route
  path="/body-analysis/otp"
  element={<CampaignOtpPage />}
/>
<Route
  path="/body-analysis/intro"
  element={
    <WizardIntroPage
      onComplete={() => {
        // فعلا تا وقتی Wizard وصل نشده
        console.log("go to wizard");
      }}
    />
  }
/>
      </Routes>
    </Router>
  );
}